import { useState, useMemo, useCallback } from 'react';
import { Dialog, Button, SelectField, useDialog, TextField, Banner, Textarea, useToast } from '@workspace/design-system/src/components';
import { Channel } from '@src/models/channel';
import { InquirySearchModal, type InquiryItem } from './inquiry-search-modal';
import { useUpsertChannelContact } from '@src/hooks/channel/mutations';
import { formatPhoneNumberInput } from '@src/utils/phone';

export interface CustomerInfoFormData {
  customerName: string;
  businessType: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  memo?: string;
}

interface CustomerInfoEditModalProps {
  channel: Channel;
  onlyEdit: boolean;
  mode?: 'required' | 'edit';
  onClose: () => void;
  onSave?: (data: CustomerInfoFormData) => Promise<void>;
  onComplete?: (updatedChannelData: Channel) => void;
}

export function CustomerInfoEditModal({
  channel,
  mode = 'edit',
  onlyEdit = false,
  onClose,
  onSave,
  onComplete
}: CustomerInfoEditModalProps) {
  const { openDialog, closeDialog: closeInnerDialog } = useDialog();
  const upsertContactMutation = useUpsertChannelContact();
  const { openToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const initialFormData = useMemo<CustomerInfoFormData>(() => ({
    customerName: channel.customerName || '',
    businessType: channel.businessType || '',
    contactName: channel.contactName || '',
    contactTitle: channel.contactTitle || '',
    contactEmail: channel.contactEmail || '',
    contactPhone: channel.contactPhone ? formatPhoneNumberInput(channel.contactPhone) : '',
    memo: channel.memo || '',
  }), [channel]);

  const [formData, setFormData] = useState<CustomerInfoFormData>(initialFormData);

  const [emailError, setEmailError] = useState('');

  const businessTypeOptions: { value: string; label: string }[] = [
    { value: 'none', label: '해당 없음' },
    { value: 'company', label: '일반 기업' },
    { value: 'agency', label: '이벤트∙광고대행사' },
    { value: 'k12', label: '초∙중∙고등학교' },
    { value: 'university', label: '대학교∙대학원' },
    { value: 'public', label: '공공기관∙지자체∙비영리단체' },
    { value: 'library', label: '도서관∙과학관∙박물관' },
  ];

  // 이메일 유효성 검사
  const validateEmail = useCallback((email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('');
      return true;
    }
    if (!emailRegex.test(email)) {
      setEmailError('올바른 이메일 형식이 아닙니다.');
      return false;
    }
    setEmailError('');
    return true;
  }, []);

  const handleInputChange = (field: keyof CustomerInfoFormData, value: string) => {
    // 전화번호 포맷팅 (required 모드)
    if (field === 'contactPhone' && mode === 'required') {
      const formatted = formatPhoneNumberInput(value);
      setFormData((prev) => ({
        ...prev,
        [field]: formatted,
      }));
      return;
    }
    
    // 전화번호 포맷팅 (edit 모드)
    if (field === 'contactPhone') {
      const formatted = formatPhoneNumberInput(value);
      setFormData((prev) => ({
        ...prev,
        [field]: formatted,
      }));
      return;
    }

    // 이메일 유효성 검사 (모든 모드)
    if (field === 'contactEmail') {
      validateEmail(value);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEmailBlur = useCallback(() => {
    validateEmail(formData.contactEmail);
  }, [formData.contactEmail, validateEmail]);

  const handleInquiryLoad = () => {
    openDialog(
      <InquirySearchModal
        onClose={closeInnerDialog}
        onSelectInquiry={(inquiry: InquiryItem) => {
          setFormData({
            customerName: inquiry.customerName || inquiry.name || '',
            businessType: inquiry.businessType || '',
            contactName: inquiry.contactName || '',
            contactTitle: inquiry.contactTitle || '',
            contactEmail: inquiry.contactEmail || '',
            contactPhone: inquiry.contactPhone || '',
            memo: inquiry.memo || formData.memo || '', // 문의 메모를 우선 사용, 없으면 기존 값 유지
          });
          closeInnerDialog();
        }}
      />
    );
  };

  // 폼 값 변경 여부
  const isDirty = useMemo(() => {
    const keys: (keyof CustomerInfoFormData)[] = [
      'customerName',
      'businessType',
      'contactName',
      'contactTitle',
      'contactEmail',
      'contactPhone',
      'memo',
    ];
    return keys.some((key) => formData[key] !== initialFormData[key]);
  }, [formData, initialFormData]);

  // 모든 필드가 입력되었는지 확인
  const isFormValid = useMemo(() => {
    const baseValid = (
      formData.customerName.trim() !== '' &&
      formData.businessType !== '' &&
      formData.contactName.trim() !== '' &&
      formData.contactTitle.trim() !== '' &&
      formData.contactEmail.trim() !== '' &&
      formData.contactPhone.trim() !== ''
    );

    // required 모드에서는 이메일 에러도 체크
    return baseValid && !emailError;
  }, [formData, emailError]);

  const handleSave = async () => {
    if (!isFormValid) return;

    setIsSaving(true);
    try {
      // onSave가 있으면 부모 컴포넌트에서 mutation 처리하도록 위임
      if (mode === 'edit' && onSave) {
        await onSave(formData);
        // onSave에서 토스트를 호출하므로 여기서는 호출하지 않음
        return;
      }

      // onSave가 없거나 required 모드인 경우 직접 mutation 처리
      await upsertContactMutation.mutateAsync({
        channelId: channel.channelId,
        body: {
          customerName: formData.customerName,
          businessType: formData.businessType,
          contactName: formData.contactName,
          contactTitle: formData.contactTitle,
          contactEmail: formData.contactEmail,
          contactPhone: formData.contactPhone,
          memo: formData.memo,
        },
      });

      if (mode === 'required') {
        if (onComplete) {
          const updatedChannelData: Channel = {
            ...channel,
            customerName: formData.customerName,
            contactName: formData.contactName,
            contactTitle: formData.contactTitle,
            contactEmail: formData.contactEmail,
            contactPhone: formData.contactPhone,
            businessType: formData.businessType,
            memo: formData.memo,
          };
          onComplete(updatedChannelData);
        }
        if (onlyEdit) {
          onClose();
        }
      }

      openToast({ type: 'confirm', message: '고객 정보가 저장되었습니다.' });
    } catch (error) {
      openToast({ type: 'destructive', message: '고객 정보 저장에 실패했습니다.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog>
      <Dialog.Content className="max-w-[1200px] max-h-[840px] flex flex-col">
        {/* 커스텀 헤더 - 제목과 버튼 양끝 배치 */}
        <div className="px-8 pt-8 pb-6 border-b border-[#F0F0F0] flex justify-between items-center">
          <h2 className="text-[20px] font-bold text-[#101010] leading-[26px]">
            {mode === 'required' ? '고객 정보 입력' : '고객 정보 수정하기'}
          </h2>
          <Button
            styleType="outline-primary"
            styleSize="S-32"
            onClick={handleInquiryLoad}
          >
            문의 건에서 불러오기
          </Button>
        </div>

        <div className="px-8 py-6 flex-1 overflow-y-auto">



          {/* 공통 폼 레이아웃 - edit 모드 디자인 사용 */}
          <div className="self-stretch flex flex-col justify-start items-start gap-6">
            {/* 고객명 */}
            <div className="self-stretch h-10 inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-center gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">고객명</div>
              </div>
              <div className="flex-1">
                <TextField
                  placeholder="기업 / 단체명을 입력해 주세요."
                  value={formData.customerName}
                  onChange={(value) => handleInputChange('customerName', value)}
                />
              </div>
            </div>

            {/* 비즈니스 유형 */}
            <div className="self-stretch h-10 inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-center gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">비즈니스 유형</div>
              </div>
              <div className="w-[492px]">
                <SelectField
                  key={`business-type-${formData.businessType}`}
                  placeholder="비즈니스 유형을 선택해 주세요."
                  value={formData.businessType}
                  onSelect={(value) => handleInputChange('businessType', String(value))}
                  items={businessTypeOptions}
                />
              </div>
            </div>

            {/* 담당자 */}
            <div className="self-stretch h-10 inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-center gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">담당자</div>
              </div>
              <div className="flex-1">
                <TextField
                  placeholder="담당자 이름을 입력해 주세요."
                  value={formData.contactName}
                  onChange={(value) => handleInputChange('contactName', value)}
                />
              </div>
            </div>

            {/* 직책 */}
            <div className="self-stretch h-10 inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-center gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">직책</div>
              </div>
              <div className="flex-1">
                <TextField
                  placeholder="담당자님의 직책을 입력해 주세요. ex) 대리"
                  value={formData.contactTitle}
                  onChange={(value) => handleInputChange('contactTitle', value)}
                />
              </div>
            </div>

            {/* 이메일 */}
            <div className="self-stretch inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-center gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">이메일</div>
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <TextField
                  type="email"
                  placeholder="담당자님의 이메일을 입력해 주세요."
                  value={formData.contactEmail}
                  onChange={(value) => handleInputChange('contactEmail', value)}
                  onBlur={handleEmailBlur}
                />
                {mode === 'required' && emailError && (
                  <span className="text-[12px] text-[#FF4D4F] leading-[16px]">{emailError}</span>
                )}
              </div>
            </div>

            {/* 연락처 */}
            <div className="self-stretch h-10 inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-center gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">연락처</div>
              </div>
              <div className="flex-1">
                <TextField
                  type="tel"
                  placeholder="담당자님의 연락처를 입력해 주세요."
                  value={formData.contactPhone}
                  onChange={(value) => handleInputChange('contactPhone', value)}
                  maxLength={mode === 'required' ? 13 : undefined}
                />
              </div>
            </div>

            {/* 메모 */}
            <div className="self-stretch inline-flex justify-start items-start gap-6">
              <div className="w-20 pt-2.5 flex justify-start items-start gap-1">
                <div className="justify-start text-[#757575] text-sm font-normal leading-5">메모(선택)</div>
              </div>
              <div className="flex-1 min-w-0">
                <Textarea
                  key={`memo-${formData.customerName}`}
                  placeholder="메모를 입력해 주세요."
                  value={formData.memo}
                  onChange={(e) => handleInputChange('memo', e.target.value)}
                  rows={4}
                  className="resize-none"

                />
              </div>
            </div>
          </div>
        </div>

        <Dialog.Footer>
          <div className="flex gap-2 justify-end">
            <Button
              styleType="outline-primary"
              styleSize="M-40"
              onClick={onClose}
            >
              취소
            </Button>
            <Button
              styleType="primary"
              styleSize="M-40"
              onClick={handleSave}
              disabled={!isFormValid || !isDirty || isSaving || upsertContactMutation.isPending}
              isLoading={isSaving || upsertContactMutation.isPending}
            >
              내용 저장
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

